import { BoundSelectCardGroupField, Css, SelectCardListGroupItemOption, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import {
  BaseBidType,
  isBaseGroup,
  nextBaseGroupClientId,
  PlanBasedBidPackageFormState,
  renumberGroups,
} from "src/routes/bids/plan-based-bid-package/utils";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";

type BaseBidSectionProps = { formState: ObjectState<PlanBasedBidPackageFormState> };

export const BaseBidSection = observer(function BaseBidSection(props: BaseBidSectionProps) {
  const { formState } = props;
  const { openModal } = useModal();
  const baseBidType = formState.baseBidType.value ?? BaseBidType.Standard;

  function onSelectBaseBidType(next: BaseBidType) {
    if (next === baseBidType) return;
    const groups = formState.bidPlanGroups.value ?? [];
    const fromStandard = baseBidType === BaseBidType.Standard;
    // Only confirm when switching would discard user-configured data: base group dimensions in
    // Standard, or zone groups in Personalization. Add-on groups (and their dimensions) are never touched.
    const needsConfirmation = fromStandard
      ? groups.some((g) => isBaseGroup(g) && (g.dimensions ?? []).nonEmpty)
      : baseBidType === BaseBidType.Personalization && groups.some(isBaseGroup);
    if (!needsConfirmation) return applyBidTypeChange(formState, next);
    openModal({
      content: (
        <ConfirmationModal
          danger
          title={fromStandard ? "Remove Base Bid?" : "Remove Personalization Zones?"}
          label="Delete"
          confirmationMessage={
            fromStandard
              ? "Switching the base bid type will delete the base bid and its cost dimensions."
              : "Switching the base bid type will delete all personalization zones."
          }
          onConfirmAction={() => applyBidTypeChange(formState, next)}
        />
      ),
    });
  }

  return (
    <>
      <BoundSelectCardGroupField
        field={formState.baseBidType}
        labelStyle="hidden"
        view="list"
        options={baseBidTypeOptions}
        onChange={onSelectBaseBidType}
      />
      {baseBidType === BaseBidType.None && (
        <div css={Css.bgGray100.br8.py3.tac.gray700.mb6.$}>Base plans will not be included in this bid.</div>
      )}
    </>
  );
});

const baseBidTypeOptions: SelectCardListGroupItemOption<BaseBidType>[] = [
  {
    value: BaseBidType.Standard,
    label: "Standard Base Bid",
    description: "Bid by cost dimensions like elevation, spec level, etc.",
  },
  {
    value: BaseBidType.Personalization,
    label: "Define Personalization Zones",
    description: "Bid by zones within the plan, each with options that affect cost (e.g. cabinets).",
  },
  { value: BaseBidType.None, label: "No Base Bid", description: "This bid covers add-on options only." },
];

/**
 * Updates bid plan groups to match the selected base bid type. Removes existing base groups and recreates
 * the default base group when switching to Standard. Add-on groups keep their dimensions as-is.
 */
function applyBidTypeChange(formState: ObjectState<PlanBasedBidPackageFormState>, next: BaseBidType) {
  let newGroups = (formState.bidPlanGroups.value ?? []).filter((g) => !isBaseGroup(g));
  if (next === BaseBidType.Standard) {
    newGroups = [
      { clientId: nextBaseGroupClientId(newGroups), order: 0, archive: false, dimensions: [] },
      ...newGroups,
    ];
  }
  formState.bidPlanGroups.set(renumberGroups(newGroups));
  formState.baseBidType.set(next);
}
